import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import { liveUrl } from '@/api/client';
import type { LiveMessage, CheckpointState, SiteState } from '@/api/types';

/**
 * Live telemetry over WebSocket.
 *
 * Reconnection uses exponential backoff with jitter. Without jitter, every
 * dashboard in the control room reconnects in lockstep after a restart and the
 * API is hit by a synchronised wave.
 */
export const useLiveStore = defineStore('live', () => {
  const connected = ref(false);
  const lastMessageAt = ref<number | null>(null);

  /** checkpointId -> latest level and state. */
  const levels = shallowRef(new Map<string, { powerDb: number; state: CheckpointState; at: string }>());
  /** siteId -> latest roll-up state. */
  const siteStates = shallowRef(new Map<string, SiteState>());
  /** Incremented whenever an alarm is raised or cleared, so views can refetch. */
  const alarmRevision = ref(0);

  let socket: WebSocket | null = null;
  let attempt = 0;
  let retryTimer: number | null = null;
  let subscribedSites: string[] = [];
  let shouldRun = false;

  function connect(siteIds: string[] = []): void {
    subscribedSites = siteIds;
    shouldRun = true;

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ subscribe: siteIds }));
      return;
    }
    open();
  }

  function open(): void {
    const url = liveUrl(subscribedSites);
    if (!url) return;

    socket = new WebSocket(url);

    socket.onopen = () => {
      connected.value = true;
      attempt = 0;
    };

    socket.onmessage = (event) => {
      lastMessageAt.value = Date.now();
      let msg: LiveMessage;
      try {
        msg = JSON.parse(event.data as string) as LiveMessage;
      } catch {
        return;
      }
      apply(msg);
    };

    socket.onclose = () => {
      connected.value = false;
      socket = null;
      if (shouldRun) scheduleReconnect();
    };

    socket.onerror = () => {
      // onclose always follows, so reconnection is handled in one place.
      socket?.close();
    };
  }

  function apply(msg: LiveMessage): void {
    switch (msg.kind) {
      case 'levels': {
        // Replacing the Map (shallowRef) rather than mutating it keeps Vue's
        // reactivity cheap: one trigger per batch instead of one per sample.
        const next = new Map(levels.value);
        for (const s of msg.samples) {
          next.set(s.checkpointId, { powerDb: s.powerDb, state: s.state, at: s.at });
        }
        levels.value = next;
        break;
      }
      case 'checkpoint_state': {
        const next = new Map(levels.value);
        for (const c of msg.changes) {
          next.set(c.checkpointId, { powerDb: c.powerDb, state: c.state, at: c.at });
        }
        levels.value = next;
        break;
      }
      case 'site_status': {
        const next = new Map(siteStates.value);
        next.set(msg.siteId, msg.state);
        siteStates.value = next;
        break;
      }
      case 'alarms':
        alarmRevision.value += 1;
        break;
      default:
        break;
    }
  }

  function scheduleReconnect(): void {
    if (retryTimer !== null) return;
    attempt += 1;
    const base = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5));
    const delay = base * (0.5 + Math.random() * 0.5);
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      if (shouldRun) open();
    }, delay);
  }

  function disconnect(): void {
    shouldRun = false;
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    socket?.close();
    socket = null;
    connected.value = false;
  }

  return {
    connected, lastMessageAt, levels, siteStates, alarmRevision,
    connect, disconnect,
  };
});
